import { HeaderPage, InputForm, LinkButton, Paginator, TableGeneric, ToggleUserStatusCell } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IColumn, IListPaginated } from '@interfaces/index'
import { IUserManagement } from '@interfaces/userManagement/IUserManagement'
import { USER_ROLE } from '@interfaces/userManagement/enums/userRole'
import { IUserFilters } from '@interfaces/userManagement/iUserFilters'
import { errorHandler } from '@services/index'
import UserManagementService from '@services/requests/userManagementService'
import { IParamsUrl } from '@utils/getQueryParams/getQueryParams'
import { getQueryParams, totalPages } from '@utils/index'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineSearch } from 'react-icons/ai'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import userRoleParameters from './userRoleParameters'

export default function Users(props: { role: USER_ROLE }): React.ReactElement {

    const paramsUrl = getQueryParams()
    const [isLoading, setIsLoading] = React.useState(false)
    const [data, setData] = React.useState<IListPaginated<IUserManagement>>(defaultPagination)

    const navigate = useNavigate()

    const paths = {
        [USER_ROLE.ADMIN]: '/administradores',
        [USER_ROLE.REALTOR]: '/corretores',
        [USER_ROLE.CLIENT]: '/clientes',
    }

    const { handleSubmit, control, formState: { isDirty }, reset } = useForm({
        defaultValues: { search: '' }
    })

    React.useEffect(() => {
        if (paramsUrl) {
            getList(paramsUrl)
            reset(paramsUrl)
        }
    }, [paramsUrl])

    function submit(values: IUserFilters): void {
        if (isDirty) {
            navigate(
                {
                    pathname: paths[props.role],
                    search: createSearchParams({
                        ...values,
                        page: '1',
                    }).toString()
                }
            )
        }
    }

    async function onPageChangeHandler(nextPage: number): Promise<void> {
        navigate(
            {
                pathname: paths[props.role],
                search: createSearchParams({
                    ...paramsUrl,
                    page: nextPage.toString(),
                }).toString()
            }
        )

    }

    async function getList(filters: IParamsUrl): Promise<void> {
        setIsLoading(true)

        try {
            const response = await UserManagementService.getUsersList(parseInt(filters.page || '1'), 25, {
                role: props.role || '',
                search: filters.search
            })
            setData(response)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    const columns: IColumn<IUserManagement>[] = React.useMemo<IColumn<IUserManagement>[]>(() => [
        {
            header: 'Nome',
            dataIndex: 'name',
            paddingRight: '15px'
        },
        {
            header: 'E-mail',
            dataIndex: 'email',
            paddingRight: '15px'
        },
        {
            header: 'Status',
            dataIndex: 'status',
            minWidth: '120px',
            align: 'align-center',
            render: (item) => (<ToggleUserStatusCell item={item} />)
        },
        {
            header: 'Ação',
            dataIndex: 'actions',
            align: 'align-center',
            size: '8rem',
            render: (item) => (
                <LinkButton title='Editar' path={`${userRoleParameters[props.role].baseUrl}/${item.id}`} />
            ),
        },
    ], [data])

    return (
        <div className="users-list card">
            <HeaderPage
                title={userRoleParameters[props.role].listTitle}
                textButton={userRoleParameters[props.role].buttonLabel}
                onClickButton={() => navigate(userRoleParameters[props.role].buttonLink || '')}
            />
            <div>
                <form className="realtor-filters" onSubmit={handleSubmit(submit)}>
                    <Controller
                        control={control}
                        name="search"
                        render={({ field: { value, onChange } }) => (
                            <InputForm
                                size="small"
                                value={value}
                                onChange={onChange}
                                onInputCallBack={() => {
                                    handleSubmit(submit)()
                                }}
                                onBlur={handleSubmit(submit)}
                                placeholder="Buscar por nome ou e-mail"
                                style={{ width: '23rem', marginRight: '1rem' }}
                                InputProps={{
                                    endAdornment: (
                                        <button type='submit' className='button-blank'>
                                            <AiOutlineSearch />
                                        </button>
                                    )
                                }}
                            />
                        )}
                    />
                </form>
                <div>
                    <TableGeneric data={data.items} columns={columns} isLoading={isLoading} />
                    <div className="paginator">
                        <Paginator
                            maxElements={7}
                            current={data.page}
                            total={totalPages(data.count, data.perPage)}
                            onClick={(page) => onPageChangeHandler(page)}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
